/**
 * API Client with automatic camelCase ↔ snake_case transformation.
 *
 * - Responses from /api/python/* are transformed from snake_case → camelCase
 * - Request bodies sent to /api/python/* are transformed from camelCase → snake_case
 * - /api/dotnet/* routes are passed through unchanged (ASP.NET already uses camelCase)
 */

// ─── Key Transformation Utilities ──────────────────────────────────────────────

/**
 * Convert a snake_case string to camelCase.
 * E.g. "total_chunks" → "totalChunks"
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 * E.g. "totalChunks" → "total_chunks"
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

/**
 * Recursively transform all keys in an object/array using the given key transformer.
 */
function transformKeys(
  data: unknown,
  keyTransformer: (key: string) => string,
): unknown {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) {
    return data.map((item) => transformKeys(item, keyTransformer));
  }
  if (typeof data === 'object' && !(data instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      result[keyTransformer(key)] = transformKeys(value, keyTransformer);
    }
    return result;
  }
  return data;
}

/**
 * Transform snake_case keys to camelCase (for Python API responses).
 */
export function toCamelCase<T>(data: unknown): T {
  return transformKeys(data, snakeToCamel) as T;
}

/**
 * Transform camelCase keys to snake_case (for Python API request bodies).
 */
export function toSnakeCase(data: unknown): unknown {
  return transformKeys(data, camelToSnake);
}

// ─── API Client ────────────────────────────────────────────────────────────────

function isPythonRoute(url: string): boolean {
  return url.startsWith('/api/python/');
}

interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  token?: string | null;
  body?: unknown;
}

/**
 * Typed fetch wrapper with automatic naming convention handling.
 *
 * Usage:
 * ```ts
 * const data = await apiClient.get<MyType>('/api/python/interview/sessions', { token });
 * const result = await apiClient.post<ResponseType>('/api/python/interview/chat', {
 *   token,
 *   body: { sessionId: '...', userMessage: '...' },
 * });
 * ```
 */
export const apiClient = {
  async get<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(url, { ...options, method: 'GET' });
  },

  async post<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(url, { ...options, method: 'POST' });
  },

  async put<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(url, { ...options, method: 'PUT' });
  },

  async delete<T = void>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(url, { ...options, method: 'DELETE' });
  },
};

async function apiRequest<T>(
  url: string,
  options: ApiRequestOptions & { method: string },
): Promise<T> {
  const { token, body, method, ...fetchOptions } = options;
  const python = isPythonRoute(url);

  const headers: HeadersInit = { ...(fetchOptions.headers as Record<string, string> ?? {}) };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = {
    ...fetchOptions,
    method,
    headers,
  };

  if (body !== undefined) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
    // Transform camelCase → snake_case for Python routes
    init.body = JSON.stringify(python ? toSnakeCase(body) : body);
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new ApiError(response.status, errorText || `HTTP ${response.status}`);
  }

  // Handle empty responses (204, etc.)
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return undefined as T;
  }

  const data = await response.json();

  // Transform snake_case → camelCase for Python route responses
  return python ? toCamelCase<T>(data) : (data as T);
}

/**
 * API error with HTTP status code for error handling in stores.
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}
